import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/core';
import { Button, Checkbox } from '@lego/klik-react';
import { Select } from '@lego/klik-ui';
import { matchSorter } from 'match-sorter';
import { spacing, media } from '../../config';
import { OverviewTable } from './overview-table/OverviewTable';
import { AssessmentExportMenu } from './components/AssessmentExportMenu';
import { TableRowCounter } from './components/TableRowCounter';
import { TableSearchField } from './components/TableSearchField';
import { Divider } from '../Divider';
import { OverlayPortal } from '../OverlayPortal';
import { Spinner } from '../spinner/Spinner';
import { routes } from '../RouteHandler';
import { overviewColumns } from './overview-table/columns';
import { GET_ASSESSMENTS } from '../../queries/assessment';
import { useUser } from '../../providers/UserProvider';
import { Heading } from '../Heading';
import { withTransaction } from '@elastic/apm-rum-react';
import { GET_ELIGIBLE_ASSESSMENT_YEARS } from '../../queries/assessmentAdmin';
import { relevantStatusForYoy } from '../../constants/assessmentStatus';

const pendingUserFilterStatusLocalStorageKey = 'pendingUserFilterIsChecked';
const selectedYearLocalStorageKey = 'selectedYear';

const overviewWrapperStyle = css({
  marginBottom: spacing.size32,
});

const yearSelectStyle = css({
  width: '7%',
});

const headerHeadingStyle = css({
  display: 'block',
  [media.large]: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: '-20px',
  },
});

const headerActionsStyle = css({
  display: 'block',
  margin: `${spacing.size6}px 0`,
  [media.large]: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
});

const actionButtonsLeft = css({
  display: 'block',
  [media.large]: {
    display: 'flex',
    alignItems: 'center',
  },
});

const resetButtonWrapperStyle = css({
  display: 'block',
  marginTop: spacing.size4,
  [media.large]: {
    marginTop: 0,
  },
});

const tableSearchWrapperStyle = css({
  marginRight: spacing.size6,
});

const tableInfoAndExportStyle = css({
  display: 'flex',
  margin: `${spacing.size6}px 0`,
  justifyContent: 'space-between',
  alignItems: 'center',
});

const channelSelectStyle = css({
  width: '180px',
  marginRight: spacing.size6,
});

const businessUnitSelectStyle = css({
  width: '180px',
  marginRight: spacing.size6,
});

const myActionCheckStyle = css({
  marginRight: spacing.size6,
});

const filterByPendingUser = (allAssessments, roles) => {
  return allAssessments.filter((assessment) =>
    roles.some((role) => role === assessment.pendingUser)
  );
};
const hasActiveFilters = (initialFilterState, currentFilters) => {
  const isPendingUserActive = currentFilters.pendingUser;
  const isSearchTextActive = currentFilters.searchText;
  const isChannelActive = currentFilters.channel !== defaultChannelFilter;
  const isBusinessUnitActive =
    currentFilters.businessUnit !== defaultBusinessUnitFilter;

  if (
    isPendingUserActive ||
    isSearchTextActive ||
    isChannelActive ||
    isBusinessUnitActive
  )
    return true;
  return false;
};

const determineDefaultYear = () => {
  const currentDate = new Date();
  const defaultYear = currentDate.getFullYear();
  const monthOfYear = currentDate.getMonth() + 1;
  return monthOfYear < 8 ? defaultYear - 1 : defaultYear;
};

export const tablePageSizes = [15, 25, 50, 100, 200];

const defaultChannelFilter = 'ALL_CHANNELS';
const defaultBusinessUnitFilter = 'ALL_BUs';

const Overview = () => {
  const pendingUserStatusFromLocalStorage = useMemo(() => {
    const pendingUserStatus = window.localStorage.getItem(
      pendingUserFilterStatusLocalStorageKey
    );

    if (pendingUserStatus) return JSON.parse(pendingUserStatus);
    return false;
  }, []);

  const selectedYearForLocalStorage = useMemo(() => {
    const selectedYear = window.localStorage.getItem(
      selectedYearLocalStorageKey
    );
    return selectedYear ? JSON.parse(selectedYear) : determineDefaultYear();
  }, []);

  const initialFilterState = {
    pendingUser: pendingUserStatusFromLocalStorage,
    searchText: '',
    channel: defaultChannelFilter,
    businessUnit: defaultBusinessUnitFilter,
    yoyDifference: false,
  };

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      window.localStorage.removeItem(selectedYearLocalStorageKey);
    });
  }, []);

  const [filters, updateFilters] = useState(initialFilterState);
  const [queryYear, setQueryYear] = useState(selectedYearForLocalStorage);

  const history = useHistory();
  const { isLoggedIn, roles } = useUser();

  const {
    data: overviewData,
    loading: isOverviewDataLoading,
    error: overviewDataError,
  } = useQuery(GET_ASSESSMENTS, {
    variables: {
      year: queryYear,
    },
    fetchPolicy: 'network-only',
    skip: !isLoggedIn,
  });

  const {
    data: eligibleYears,
    loading: isEligibleYearsLoading,
    error: eligibleYearsError,
  } = useQuery(GET_ELIGIBLE_ASSESSMENT_YEARS, {
    skip: !isLoggedIn,
  });

  if (eligibleYearsError) {
    throw eligibleYearsError;
  }

  // Extract unique channels from assessment data
  const uniqueChannels = useMemo(() => {
    if (!overviewData || !overviewData.getAssessments) return [];

    const channels = overviewData.getAssessments
      .map((assessment) => assessment.channel)
      .filter(Boolean) // Remove null/undefined values
      .reduce((unique, channel) => {
        if (!unique.includes(channel)) {
          unique.push(channel);
        }
        return unique;
      }, []);

    return channels.sort();
  }, [overviewData]);

  // Extract unique business units from assessment data
  const uniqueBusinessUnits = useMemo(() => {
    if (!overviewData || !overviewData.getAssessments) return [];

    const businessUnits = overviewData.getAssessments
      .map((assessment) => assessment.businessUnit)
      .filter(Boolean) // Remove null/undefined values
      .reduce((unique, businessUnit) => {
        if (!unique.includes(businessUnit)) {
          unique.push(businessUnit);
        }
        return unique;
      }, []);

    return businessUnits.sort();
  }, [overviewData]);

  const handleTableRowClick = (rowData) => {
    const { customerId, year, version } = rowData;
    history.push(routes.details(customerId, year, version));
  };

  const handleTextSearchUpdate = (searchText) =>
    updateFilters((prevState) => ({
      ...prevState,
      searchText,
    }));

  const handleChannelChange = (e) => {
    updateFilters((prevState) => ({
      ...prevState,
      channel: e.target.value,
    }));
  };

  const handleBusinessUnitChange = (e) => {
    updateFilters((prevState) => ({
      ...prevState,
      businessUnit: e.target.value,
    }));
  };

  const filter = (assessments, filters, roles) => {
    const { pendingUser, searchText, channel, businessUnit, yoyDifference } =
      filters;

    let filteredData = [...assessments];
    if (pendingUser) {
      filteredData = filterByPendingUser(assessments, roles);
    }

    if (searchText) {
      filteredData = matchSorter(filteredData, searchText, {
        keys: ['customerId', 'customerName', 'c6Name'],
      });
    }

    if (channel && channel !== defaultChannelFilter) {
      filteredData = filteredData.filter(
        (assessment) => assessment.channel === channel
      );
    }

    if (businessUnit && businessUnit !== defaultBusinessUnitFilter) {
      filteredData = filteredData.filter(
        (assessment) => assessment.businessUnit === businessUnit
      );
    }

    if (yoyDifference) {
      filteredData = filteredData.filter(
        (assessment) =>
          assessment.lastYearScore !== undefined &&
          assessment.lastYearScore !== null &&
          assessment.score - assessment.lastYearScore !== 0 &&
          relevantStatusForYoy(assessment.status)
      );
    }

    return filteredData;
  };

  const columns = useMemo(() => overviewColumns, []);

  // Error boundary catches this error
  if (overviewDataError) throw overviewDataError;

  if (isOverviewDataLoading || !isLoggedIn) {
    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  const filteredTableData = filter(overviewData.getAssessments, filters, roles);

  const handleYearSelect = (value) => {
    window.localStorage.setItem(selectedYearLocalStorageKey, value);
    setQueryYear(value);
  };

  return (
    <div css={overviewWrapperStyle}>
      <div css={headerHeadingStyle}>
        <Heading type="h1" text="Assessment Overview" />
        <div css={yearSelectStyle}>
          <Select
            value={queryYear}
            onChange={(value) => handleYearSelect(parseInt(value.target.value))}
          >
            {!isEligibleYearsLoading &&
              eligibleYears?.getEligibleAssessmentYears &&
              eligibleYears.getEligibleAssessmentYears.map(({ year }) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
          </Select>
        </div>
      </div>
      <div css={headerActionsStyle}>
        <div css={actionButtonsLeft}>
          <span css={tableSearchWrapperStyle}>
            <TableSearchField
              onChange={handleTextSearchUpdate}
              value={filters.searchText}
            />
          </span>
          <span css={channelSelectStyle}>
            <Select value={filters.channel} onChange={handleChannelChange}>
              <option value={defaultChannelFilter}>All Channels</option>
              {uniqueChannels.map((channel) => (
                <option key={channel} value={channel}>
                  {channel}
                </option>
              ))}
            </Select>
          </span>
          <span css={businessUnitSelectStyle}>
            <Select
              value={filters.businessUnit}
              onChange={handleBusinessUnitChange}
            >
              <option value={defaultBusinessUnitFilter}>All BUs</option>
              {uniqueBusinessUnits.map((businessUnit) => (
                <option key={businessUnit} value={businessUnit}>
                  {businessUnit}
                </option>
              ))}
            </Select>
          </span>
          <span css={myActionCheckStyle}>
            <Checkbox
              label="Show only my action required"
              checked={filters.pendingUser}
              onChange={() =>
                updateFilters((prevState) => {
                  window.localStorage.setItem(
                    pendingUserFilterStatusLocalStorageKey,
                    !prevState.pendingUser
                  );
                  return {
                    ...prevState,
                    pendingUser: !prevState.pendingUser,
                  };
                })
              }
            />
          </span>
          <Checkbox
            label="Show only YoY changes"
            checked={filters.yoyDifference}
            onChange={() =>
              updateFilters((prevState) => {
                return {
                  ...prevState,
                  yoyDifference: !prevState.yoyDifference,
                };
              })
            }
          />
        </div>
        <span css={resetButtonWrapperStyle}>
          <Button
            label="Reset filters"
            variant="ghost"
            disabled={!hasActiveFilters(initialFilterState, filters)}
            onClick={() => {
              window.localStorage.setItem(
                pendingUserFilterStatusLocalStorageKey,
                false
              );
              updateFilters({
                ...initialFilterState,
                pendingUser: false,
              });
            }}
          />
        </span>
      </div>
      <Divider />
      <div css={tableInfoAndExportStyle}>
        <TableRowCounter
          numberOfFilteredRows={filteredTableData.length}
          searchText={filters.searchText}
        />
        <AssessmentExportMenu
          assessments={overviewData.getAssessments}
          year={queryYear}
        />
      </div>
      <OverviewTable
        rowClick={handleTableRowClick}
        pageSizes={tablePageSizes}
        overviewTableData={filteredTableData}
        columns={columns}
      />
    </div>
  );
};

const OverviewPage = withTransaction('OverviewPage', 'component')(Overview);
export { OverviewPage, pendingUserFilterStatusLocalStorageKey };
